import React, { useCallback, useState } from 'react';
import { Icon } from 'antd';
import styled, { css } from 'styled-components';
import { useAuthCtx } from '../auth_v2/hooks/useAuthCtx';
import {
  AISummaryData,
  getBookChapterSummary,
  getPeriodicItemSummary
} from './aiSummaryApi';
import { AISummaryModal } from './AISummaryModal';
import { FORUM_IAH_SALES_URL } from './constants';

export type AISummarySource =
  | {
      kind: 'book';
      bookId: string | number;
      editionId: string | number;
      chapterId: string | number;
    }
  | {
      kind: 'periodic';
      periodicId: string | number;
      editionId: string | number;
      itemId: string | number;
    };

interface IProps {
  source: AISummarySource;
  variant?: 'compact' | 'inline';
}

const compactStyle = css`
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  border-radius: 999px;
`;

const inlineStyle = css`
  padding: 0.55rem 1.25rem;
  font-size: 0.9rem;
  border-radius: 6px;
`;

const ButtonBase = styled.button<{ $variant: 'compact' | 'inline' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: linear-gradient(135deg, #ebf2ff 0%, #f3eaff 100%);
  border: 1px solid #c8d6ff;
  color: #1d3a8a;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 0.2s, transform 0.05s;

  ${(p) => (p.$variant === 'compact' ? compactStyle : inlineStyle)}

  &:hover {
    filter: brightness(0.97);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: progress;
  }

  i {
    color: #4f46e5;
  }
`;

const ButtonLink = ButtonBase.withComponent('a');

export const AISummaryButton = ({ source, variant = 'compact' }: IProps) => {
  const { hasAIAccess, aiAccessLoading } = useAuthCtx();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AISummaryData | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    setData(null);
    try {
      let result: AISummaryData;
      if (source.kind === 'book') {
        result = await getBookChapterSummary({
          bookId: source.bookId,
          editionId: source.editionId,
          chapterId: source.chapterId
        });
      } else {
        result = await getPeriodicItemSummary({
          periodicId: source.periodicId,
          editionId: source.editionId,
          itemId: source.itemId
        });
      }
      setData(result);
    } catch (e: any) {
      const status = e && e.response && e.response.status;
      if (status === 404) {
        setNotFound(true);
      } else if (status === 403) {
        window.open(FORUM_IAH_SALES_URL, '_blank', 'noopener,noreferrer');
        setVisible(false);
        return;
      } else {
        setError('Não foi possível carregar o resumo agora. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }, [source]);

  if (aiAccessLoading) {
    return (
      <ButtonBase
        type="button"
        $variant={variant}
        disabled
        onClick={(e) => e.stopPropagation()}>
        <Icon type="loading" />
        Resumo com IA
      </ButtonBase>
    );
  }

  if (!hasAIAccess) {
    return (
      <ButtonLink
        $variant={variant}
        href={FORUM_IAH_SALES_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}>
        <Icon type="thunderbolt" />
        Resumo com IA
      </ButtonLink>
    );
  }

  return (
    <>
      <ButtonBase
        type="button"
        $variant={variant}
        onClick={(e) => {
          e.stopPropagation();
          setVisible(true);
          fetchSummary();
        }}>
        <Icon type="thunderbolt" />
        Resumo com IA
      </ButtonBase>

      <AISummaryModal
        visible={visible}
        onClose={() => setVisible(false)}
        loading={loading}
        notFound={notFound}
        error={error}
        data={data}
      />
    </>
  );
};
